import * as yup from 'yup';
import { ThirdPartyFormValues } from 'components/organisms/third-party-form-modal/types';
import { alphanumeric } from 'helpers/yup-validations';
import { ThirdPartyDataConfigDictionary } from 'services/getThirdPartyConfiguration';
import { Gender, ThirdPartyType } from 'types';
import { TerminologyConfigType } from 'context/types';
import dayjs from 'dayjs';

export const initialState: ThirdPartyFormValues = {
  type: ThirdPartyType.Business,
  name: '',
  alternativeName: '',
  internalId: undefined,
  duns: undefined,
  primaryCountry: undefined,
  keyPhoneNumber: undefined,
  countryTwo: undefined,
  countryThree: undefined,
  nationality: undefined,
  companyUrl: undefined,
  dateOfBirth: undefined,
  addressLineOne: undefined,
  city: undefined,
  county: undefined,
  zipCode: undefined,
  gender: Gender.Unknown,
  registrationNumber: undefined,
  swift: undefined,
  passportNumber: undefined,
  nationalIDNumber: undefined,
  otherIDNumber: undefined,
  ownerIds: [],
};

interface ValidationSchemaParams {
  thirdPartyDataConfig: ThirdPartyDataConfigDictionary;
  terminologyConfig: TerminologyConfigType;
}

export const validationSchema = ({
  thirdPartyDataConfig,
  terminologyConfig,
}: ValidationSchemaParams) =>
  yup.object().shape({
    type: yup.string().required('Please enter a message'),
    name: alphanumeric.max(256, 'Maximum 256 characters').test('name-required', function () {
      const { type } = this.parent;
      if (thirdPartyDataConfig.name?.isMandatory && !this.parent.name) {
        return this.createError({
          message: `Please enter ${
            type === ThirdPartyType.Business ? 'a company' : 'an individual'
          } name`,
        });
      }
      return true;
    }),
    alternativeName: alphanumeric
      .max(140, 'Maximum 140 characters')
      .nullable()
      .test('alternativeName-required', function () {
        const { type } = this.parent;
        if (thirdPartyDataConfig.alternativeName?.isMandatory) {
          return this.createError({
            message: `Please enter ${
              type === ThirdPartyType.Business
                ? 'an alternative company'
                : 'an alternative individual'
            } name`,
          });
        }
        return true;
      }),
    primaryCountry: yup.mixed().when('type', {
      is: () => thirdPartyDataConfig.primaryCountry?.isMandatory,
      then: (schema) =>
        schema.test('screening-setting', 'Please select one!', (value) => {
          return value !== 'DEFAULT';
        }),
    }),
    internalId: yup
      .string()
      .max(32, 'Maximum 32 characters')
      .when('type', {
        is: () => thirdPartyDataConfig.internalId?.isMandatory,
        then: (schema) =>
          schema.required(`Please enter ${terminologyConfig?.internalIdSingularNaming} name`),
      }),
    duns: yup
      .string()
      .max(19, 'Maximum 19 characters')
      .nullable()
      .test('duns-number', 'Duns needs to be a number', (value) => {
        if (!value || value.length === 0) {
          return true;
        }
        const Regex = new RegExp(/^[0-9]*$/);
        return Regex.test(value);
      })
      .when('type', {
        is: (type: ThirdPartyType) =>
          type === ThirdPartyType.Business && thirdPartyDataConfig.duns?.isMandatory,
        then: (schema) => schema.required(`Please enter DUNS`),
      }),
    addressLineOne: yup
      .string()
      .max(140, 'Maximum 140 characters')
      .nullable()
      .when('type', {
        is: () => thirdPartyDataConfig.addressLineOne?.isMandatory,
        then: (schema) => schema.required(`Please enter an address`),
      }),
    city: yup
      .string()
      .max(60, 'Maximum 60 characters')
      .when('type', {
        is: () => thirdPartyDataConfig.city?.isMandatory,
        then: (schema) => schema.required(`Please enter a city`),
      }),
    county: yup
      .string()
      .max(60, 'Maximum 60 characters')
      .when('type', {
        is: () => thirdPartyDataConfig.county?.isMandatory,
        then: (schema) => schema.required(`Please enter a state, region and/or province`),
      }),
    zipCode: yup
      .string()
      .max(10, 'Maximum 10 characters')
      .nullable()
      .when('type', {
        is: () => thirdPartyDataConfig.zipCode?.isMandatory,
        then: (schema) => schema.required(`Please enter a post code or zip code`),
      }),
    countryTwo: yup.mixed().when('type', {
      is: () => thirdPartyDataConfig.countryTwo?.isMandatory,
      then: (schema) =>
        schema.test('screening-setting', 'Please select one!', (value) => {
          return value !== 'DEFAULT';
        }),
    }),
    countryThree: yup.mixed().when('type', {
      is: () => thirdPartyDataConfig.countryThree?.isMandatory,
      then: (schema) =>
        schema.test('screening-setting', 'Please select one!', (value) => {
          return value !== 'DEFAULT';
        }),
    }),
    gender: yup
      .number()
      .nullable()
      .typeError('Please select a gender')
      .when('type', {
        is: (type: ThirdPartyType) =>
          type === ThirdPartyType.Individual && thirdPartyDataConfig.gender?.isMandatory,
        then: (schema) => schema.required('Please select a gender'),
      }),
    dateOfBirth: yup
      .string()
      .nullable()
      .test('valid-date', 'Must be a valid date', (value) => {
        if (!value) {
          return true;
        }
        return dayjs(value, 'DD/MM/YYYY').isValid();
      })
      .when('type', {
        is: (type: ThirdPartyType) =>
          type === ThirdPartyType.Individual && thirdPartyDataConfig.dateOfBirth?.isMandatory,
        then: (schema) => schema.required(`Please enter a date of birth`),
      }),
    nationality: yup.mixed().when('type', {
      is: (type: ThirdPartyType) =>
        type === ThirdPartyType.Individual && thirdPartyDataConfig.nationality?.isMandatory,
      then: (schema) =>
        schema.test('screening-setting', 'Please select one!', (value) => {
          return value !== 'DEFAULT';
        }),
    }),
    passportNumber: yup
      .string()
      .max(20, 'Maximum 20 characters')
      .when('type', {
        is: (type: ThirdPartyType) =>
          type === ThirdPartyType.Individual && thirdPartyDataConfig.passportNumber?.isMandatory,
        then: (schema) => schema.required(`Please enter a passport number`),
      }),
    nationalIDNumber: yup
      .string()
      .max(20, 'Maximum 20 characters')
      .when('type', {
        is: (type: ThirdPartyType) =>
          type === ThirdPartyType.Individual && thirdPartyDataConfig.nationalIDNumber?.isMandatory,
        then: (schema) => schema.required(`Please enter a national ID number`),
      }),
    otherIDNumber: yup
      .string()
      .max(20, 'Maximum 20 characters')
      .when('type', {
        is: (type: ThirdPartyType) =>
          type === ThirdPartyType.Individual && thirdPartyDataConfig.otherIDNumber?.isMandatory,
        then: (schema) => schema.required(`Please enter other ID number`),
      }),
    registrationNumber: yup
      .string()
      .max(25, 'Maximum 25 characters')
      .nullable()
      .when('type', {
        is: (type: ThirdPartyType) =>
          type === ThirdPartyType.Business && thirdPartyDataConfig.registrationNumber?.isMandatory,
        then: (schema) => schema.required(`Please enter a registration number`),
      }),
    swift: yup
      .string()
      .max(20, 'Maximum 20 characters')
      .nullable()
      .when('type', {
        is: (type: ThirdPartyType) =>
          type === ThirdPartyType.Business && thirdPartyDataConfig.swift?.isMandatory,
        then: (schema) => schema.required(`Please enter a SWIFT or BIC number`),
      }),
    companyUrl: yup
      .string()
      .max(255, 'Maximum 255 characters')
      .nullable()
      .when('type', {
        is: (type: ThirdPartyType) =>
          type === ThirdPartyType.Business && thirdPartyDataConfig.companyUrl?.isMandatory,
        then: (schema) => schema.required(`Please enter a company website`),
      }),
  });
